import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { userAtom } from '../store/atoms';
import axiosService from '../services/axios.service';
import { useCrewModuleEnabled } from './useCrews';
import type {
  RetroDTO,
  RetroBoardDto,
  RetroCardDto,
  RetroActionItem,
  RetroTimerState,
  RetroTimerDto,
  CreateRetroDto,
  UpdateRetroDto,
  CreateRetroCardDto,
  UpdateRetroCardDto,
  RetroVoteDto,
  AddRetroCommentDto,
  CreateRetroActionItemDto,
  UpdateRetroActionItemDto,
} from '../../../shared/types/retro';

// ─────────────────────────────────────────────
// Query key factory
// ─────────────────────────────────────────────

export const retroKeys = {
  all: ['retros'] as const,
  crew: (crewSlug: string) => [...retroKeys.all, crewSlug] as const,
  list: (crewSlug: string, teamId?: string) =>
    [...retroKeys.crew(crewSlug), 'list', teamId] as const,
  board: (crewSlug: string, retroId: string) =>
    [...retroKeys.crew(crewSlug), 'board', retroId] as const,
};

// ─────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────

export function useRetros(crewSlug: string, teamId?: string) {
  const user = useAtomValue(userAtom);
  const retrosEnabled = useCrewModuleEnabled(crewSlug, 'retrospectives');
  return useQuery<RetroDTO[]>({
    queryKey: retroKeys.list(crewSlug, teamId),
    queryFn: async () => {
      const params = teamId ? { teamId } : {};
      const { data } = await axiosService.get(`/c/${crewSlug}/retros`, { params });
      return data.data;
    },
    enabled: !!crewSlug && !!user && retrosEnabled,
  });
}

export function useRetroBoard(crewSlug: string, retroId: string) {
  const user = useAtomValue(userAtom);
  return useQuery<RetroBoardDto>({
    queryKey: retroKeys.board(crewSlug, retroId),
    queryFn: async () => {
      const { data } = await axiosService.get(`/c/${crewSlug}/retros/${retroId}`);
      return data.data;
    },
    enabled: !!crewSlug && !!retroId && !!user,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 3;
    },
  });
}

export function useCreateRetro(crewSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateRetroDto) =>
      axiosService.post(`/c/${crewSlug}/retros`, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retroKeys.crew(crewSlug) });
    },
  });
}

export function useUpdateRetro(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateRetroDto) =>
      axiosService.put(`/c/${crewSlug}/retros/${retroId}`, dto),
    onSuccess: (res) => {
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
        if (!old) return old;
        return { ...old, retro: res.data.data };
      });
    },
  });
}

export function useArchiveRetro(crewSlug: string, teamId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (retroId: string) =>
      axiosService.post(`/c/${crewSlug}/retros/${retroId}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retroKeys.list(crewSlug, teamId) });
    },
  });
}

export function useConcludeRetro(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => axiosService.post(`/c/${crewSlug}/retros/${retroId}/conclude`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: retroKeys.board(crewSlug, retroId) });
      queryClient.invalidateQueries({ queryKey: retroKeys.crew(crewSlug) });
    },
  });
}

export function useJoinRetro(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => axiosService.post(`/c/${crewSlug}/retros/${retroId}/join`),
    onSuccess: (res) => {
      queryClient.setQueryData(retroKeys.board(crewSlug, retroId), res.data.data);
    },
  });
}

export function useLeaveRetro(crewSlug: string, retroId: string) {
  return useMutation({
    mutationFn: () => axiosService.post(`/c/${crewSlug}/retros/${retroId}/leave`),
  });
}

export function useAddRetroCard(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateRetroCardDto) =>
      axiosService.post(`/c/${crewSlug}/retros/${retroId}/cards`, dto),
    onSuccess: (res) => {
      const card = res.data.data as RetroCardDto;
      // Insert immediately from HTTP response; SSE delivers to other participants
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
        if (!old) return old;
        const exists = old.cards.some(c => c._id === card._id);
        if (exists) return old;
        return { ...old, cards: [...old.cards, card] };
      });
    },
  });
}

export function useUpdateRetroCard(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, dto }: { cardId: string; dto: UpdateRetroCardDto }) =>
      axiosService.put(`/c/${crewSlug}/retros/${retroId}/cards/${cardId}`, dto),
    onMutate: ({ cardId, dto }) => {
      // Optimistic update for own card edits
      const prev = queryClient.getQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId));
      if (prev) {
        queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
          if (!old) return old;

          const original = old.cards.find(c => c._id === cardId);
          const oldGroupId = original?.groupId;

          const updated = old.cards.map(c => {
            if (c._id !== cardId) return c;
            return {
              ...c,
              ...(dto.text !== undefined ? { text: dto.text } : {}),
              ...(dto.columnId !== undefined ? { columnId: dto.columnId } : {}),
              ...(dto.order !== undefined ? { order: dto.order } : {}),
              ...('groupId' in dto ? { groupId: dto.groupId ?? undefined } : {}),
            };
          });

          // If the card left a group, dissolve that group if only 1 member remains
          const groupIdChanged = 'groupId' in dto && (dto.groupId ?? undefined) !== oldGroupId;
          if (groupIdChanged && oldGroupId) {
            const remaining = updated.filter(c => c.groupId === oldGroupId);
            if (remaining.length === 1) {
              return {
                ...old,
                cards: updated.map(c => c._id === remaining[0]._id ? { ...c, groupId: undefined } : c),
              };
            }
          }

          return { ...old, cards: updated };
        });
      }
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(retroKeys.board(crewSlug, retroId), context.prev);
      }
    },
  });
}

export function useDeleteRetroCard(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) =>
      axiosService.delete(`/c/${crewSlug}/retros/${retroId}/cards/${cardId}`),
    onMutate: async (cardId) => {
      const prev = queryClient.getQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId));
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
        if (!old) return old;

        const deletedCard = old.cards.find(c => c._id === cardId);
        const deletedGroupId = deletedCard?.groupId;

        const remaining = old.cards.filter(c => c._id !== cardId);

        // Dissolve the group if only 1 member remains after deletion
        if (deletedGroupId) {
          const groupRemaining = remaining.filter(c => c.groupId === deletedGroupId);
          if (groupRemaining.length === 1) {
            return {
              ...old,
              cards: remaining.map(c => c._id === groupRemaining[0]._id ? { ...c, groupId: undefined } : c),
            };
          }
        }

        return { ...old, cards: remaining };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(retroKeys.board(crewSlug, retroId), context.prev);
      }
    },
  });
}

export function useVoteRetroCard(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, dto }: { cardId: string; dto: RetroVoteDto }) =>
      axiosService.post(`/c/${crewSlug}/retros/${retroId}/cards/${cardId}/vote`, dto),
    onMutate: ({ cardId, dto }) => {
      // Optimistic: update selfVote and adjust counts locally
      const prev = queryClient.getQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId));
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
        if (!old) return old;
        return {
          ...old,
          cards: old.cards.map(c => {
            if (c._id !== cardId) return c;
            const prevVote = c.selfVote;
            const upvotes = c.upvotes
              - (prevVote === 1 ? 1 : 0)
              + (dto.value === 1 ? 1 : 0);
            const downvotes = c.downvotes
              - (prevVote === -1 ? 1 : 0)
              + (dto.value === -1 ? 1 : 0);
            return { ...c, selfVote: dto.value, upvotes, downvotes };
          }),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(retroKeys.board(crewSlug, retroId), context.prev);
      }
    },
  });
}

export function useAddRetroComment(crewSlug: string, retroId: string) {
  return useMutation({
    mutationFn: ({ cardId, dto }: { cardId: string; dto: AddRetroCommentDto }) =>
      axiosService.post(`/c/${crewSlug}/retros/${retroId}/cards/${cardId}/comments`, dto),
    // SSE delivers the comment; no local optimistic update needed
  });
}

export function useAddRetroActionItem(crewSlug: string, retroId: string) {
  return useMutation({
    mutationFn: (dto: CreateRetroActionItemDto) =>
      axiosService.post(`/c/${crewSlug}/retros/${retroId}/action-items`, dto),
    // SSE delivers the updated action items list
  });
}

export function useUpdateRetroActionItem(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, dto }: { itemId: string; dto: UpdateRetroActionItemDto }) =>
      axiosService.put(`/c/${crewSlug}/retros/${retroId}/action-items/${itemId}`, dto),
    onMutate: async ({ itemId, dto }) => {
      // Optimistic toggle for completed state
      const prev = queryClient.getQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId));
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
        if (!old) return old;
        return {
          ...old,
          retro: {
            ...old.retro,
            actionItems: old.retro.actionItems.map((item: RetroActionItem) =>
              item.id === itemId ? { ...item, ...dto } : item
            ),
          },
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(retroKeys.board(crewSlug, retroId), context.prev);
      }
    },
  });
}

export function useRetroTimerAction(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: RetroTimerDto) =>
      axiosService.post(`/c/${crewSlug}/retros/${retroId}/timer`, dto),
    onSuccess: (res) => {
      const state = res.data.data as RetroTimerState;
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old =>
        old ? { ...old, timer: state } : old
      );
    },
  });
}

/** Toggle an action item's completed state from outside the board (e.g. team page aggregate).
 *  Optimistically updates the retros list cache so the team page reflects the change instantly. */
export function useToggleRetroActionItem(crewSlug: string, teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ retroId, itemId, completed }: { retroId: string; itemId: string; completed: boolean }) =>
      axiosService.put(`/c/${crewSlug}/retros/${retroId}/action-items/${itemId}`, { completed }),
    onMutate: async ({ retroId, itemId, completed }) => {
      const key = retroKeys.list(crewSlug, teamId);
      const prev = queryClient.getQueryData<RetroDTO[]>(key);
      queryClient.setQueryData<RetroDTO[]>(key, old =>
        old?.map(r => r._id !== retroId ? r : {
          ...r,
          actionItems: r.actionItems.map(item => item.id === itemId ? { ...item, completed } : item),
        })
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(retroKeys.list(crewSlug, teamId), context.prev);
    },
  });
}

export function useDeleteRetroActionItem(crewSlug: string, retroId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      axiosService.delete(`/c/${crewSlug}/retros/${retroId}/action-items/${itemId}`),
    onMutate: async (itemId) => {
      const prev = queryClient.getQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId));
      queryClient.setQueryData<RetroBoardDto>(retroKeys.board(crewSlug, retroId), old => {
        if (!old) return old;
        return {
          ...old,
          retro: {
            ...old.retro,
            actionItems: old.retro.actionItems.filter((item: RetroActionItem) => item.id !== itemId),
          },
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(retroKeys.board(crewSlug, retroId), context.prev);
      }
    },
  });
}
